import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { ExportButton } from '../components/ExportButton';
import { COLUMN_PRESETS } from '../services/exportService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  FileText, Plus, Search, Filter, MoreHorizontal, AlertTriangle,
  Scale, Clock, Eye, Edit, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ACT_TYPES = [
  { value: 'RECOMENDACAO', label: 'Recomendação' },
  { value: 'DETERMINACAO', label: 'Determinação' },
  { value: 'DILIGENCIA', label: 'Diligência' },
  { value: 'AUDITORIA_INTERNA', label: 'Auditoria Interna' },
  { value: 'AUDITORIA_EXTERNA', label: 'Auditoria Externa' },
  { value: 'TAC', label: 'TAC' },
  { value: 'ACP', label: 'ACP' },
  { value: 'SOLICITACAO_INFORMACAO', label: 'Solicitação de Informação' },
  { value: 'RELATORIO_AUDITORIA', label: 'Relatório de Auditoria' },
  { value: 'NOTIFICACAO', label: 'Notificação' },
];

const SEVERITIES = [
  { value: 'LOW', label: 'Baixa', color: 'bg-green-100 text-green-800' },
  { value: 'MEDIUM', label: 'Média', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'HIGH', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  { value: 'CRITICAL', label: 'Crítica', color: 'bg-red-100 text-red-800' },
];

const STATUSES = [
  { value: 'RECEBIDO', label: 'Recebido', color: 'bg-blue-100 text-blue-800' },
  { value: 'CLASSIFICADO', label: 'Classificado', color: 'bg-indigo-100 text-blue-800' },
  { value: 'PLANEJADO', label: 'Planejado', color: 'bg-blue-100 text-blue-800' },
  { value: 'EM_EXECUCAO', label: 'Em Execução', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'AGUARDANDO_EVIDENCIA', label: 'Aguard. Evidência', color: 'bg-orange-100 text-orange-800' },
  { value: 'EM_REVISAO', label: 'Em Revisão', color: 'bg-pink-100 text-pink-800' },
  { value: 'RESPONDIDO', label: 'Respondido', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'ENCERRADO', label: 'Encerrado', color: 'bg-green-100 text-green-800' },
  { value: 'ARQUIVADO', label: 'Arquivado', color: 'bg-slate-100 text-slate-800' },
];

export const ActsListPage = () => {
  const { api, hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    status: '',
    control_body: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchActs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', String(page * 20));
      params.append('limit', '20');
      
      if (filters.type) params.append('type', filters.type);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.status) params.append('status', filters.status);
      if (filters.control_body) params.append('control_body', filters.control_body);

      const response = await api.get(`/acts?${params.toString()}`);
      setActs(response.data);
      setHasMore(response.data.length === 20);
    } catch (error) {
      console.error('Error fetching acts:', error);
      toast.error('Erro ao carregar atos de controle');
    } finally {
      setLoading(false);
    }
  }, [api, page, filters]);

  useEffect(() => {
    fetchActs();
  }, [fetchActs]);

  const clearFilters = () => {
    setFilters({
      type: '',
      severity: '',
      status: '',
      control_body: '',
      search: ''
    });
    setPage(0);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v).length;

  const getSeverityBadge = (severity) => {
    const sev = SEVERITIES.find(s => s.value === severity);
    return <Badge className={sev?.color || ''}>{sev?.label || severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const st = STATUSES.find(s => s.value === status);
    return <Badge className={st?.color || ''}>{st?.label || status}</Badge>;
  };

  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date();
  };

  const filteredActs = acts.filter(act => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        act.control_body?.toLowerCase().includes(search) ||
        act.description?.toLowerCase().includes(search) ||
        act.thematic_area?.toLowerCase().includes(search) ||
        act.act_number?.toLowerCase().includes(search) ||
        act.act_id?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="acts-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Atos de Controle</h1>
            <p className="text-slate-500 mt-1">
              {filteredActs.length} atos encontrados
            </p>
          </div>
          <div className="flex gap-3">
            <ExportButton
              data={filteredActs}
              columns={COLUMN_PRESETS.acts}
              title="Lista de Atos de Controle"
              filename="atos-controle"
              tenant={user?.tenant_id}
              generatedBy={user?.name}
              variant="outline"
              className="border-slate-300"
            />
            {hasPermission('create') && (
              <Button
                className="bg-[#1d4ed8] hover:bg-[#1e40af]"
                onClick={() => navigate('/acts/new')}
                data-testid="create-act-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Ato
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por número do ato, órgão, descrição..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                data-testid="search-input"
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-[#1d4ed8]' : ''}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
              {activeFiltersCount > 0 && (
                <Badge className="ml-2 bg-white text-[#1d4ed8]">{activeFiltersCount}</Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
                  <SelectTrigger data-testid="filter-type">
                    <SelectValue placeholder="Tipo de Ato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todos os tipos</SelectItem>
                    {ACT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.severity} onValueChange={(v) => setFilters({ ...filters, severity: v })}>
                  <SelectTrigger data-testid="filter-severity">
                    <SelectValue placeholder="Severidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todas</SelectItem>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Todos</SelectItem>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Órgão emissor"
                  value={filters.control_body}
                  onChange={(e) => setFilters({ ...filters, control_body: e.target.value })}
                  data-testid="filter-organ"
                />
              </div>

              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-4 text-slate-500"
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar filtros
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Acts Table */}
        <Card>
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredActs.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">Nenhum ato encontrado</h3>
              <p className="empty-state-description">
                {activeFiltersCount > 0
                  ? 'Tente ajustar os filtros para encontrar atos'
                  : 'Comece criando um novo ato de controle'}
              </p>
              {hasPermission('create') && activeFiltersCount === 0 && (
                <Button
                  className="mt-4 bg-[#1d4ed8] hover:bg-[#1e40af]"
                  onClick={() => navigate('/acts/new')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Ato
                </Button>
              )}
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Número do Ato</TableHead>
                  <TableHead>Órgão Emissor</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Severidade</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActs.map((act) => (
                  <TableRow
                    key={act.act_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/acts/${act.act_id}`)}
                    data-testid={`act-row-${act.act_id}`}
                  >
                    <TableCell>
                      <span className="font-mono text-sm text-slate-700">
                        {act.act_number || act.act_id.substring(0, 12)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {act.is_judicialized && (
                          <Scale className="w-4 h-4 text-red-500" title="Judicializado" />
                        )}
                        {act.risk_level === 'CRITICAL' && (
                          <AlertTriangle className="w-4 h-4 text-red-500" title="Risco Crítico" />
                        )}
                        <span className="font-medium text-slate-900">{act.control_body}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {ACT_TYPES.find(t => t.value === act.type)?.label || act.type}
                    </TableCell>
                    <TableCell>{getSeverityBadge(act.severity)}</TableCell>
                    <TableCell>
                      <div className={`flex items-center gap-1 ${
                        isOverdue(act.deadline) && act.status !== 'ENCERRADO' && act.status !== 'ARQUIVADO'
                          ? 'text-red-600'
                          : 'text-slate-600'
                      }`}>
                        {isOverdue(act.deadline) && act.status !== 'ENCERRADO' && act.status !== 'ARQUIVADO' && (
                          <Clock className="w-4 h-4" />
                        )}
                        {format(new Date(act.deadline), 'dd/MM/yyyy', { locale: ptBR })}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(act.status)}</TableCell>
                    <TableCell className="text-slate-600">
                      {act.responsible_unit || '-'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" data-testid={`act-menu-${act.act_id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/acts/${act.act_id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Visualizar
                          </DropdownMenuItem>
                          {hasPermission('edit') && (
                            <DropdownMenuItem onClick={() => navigate(`/acts/${act.act_id}/edit`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {!loading && filteredActs.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Página {page + 1}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
};

export default ActsListPage;
